// docs/21 §7 the intro's story lexicon: a deterministic lookup from the storyteller's words to what
// the client can draw. Each panel's heading, narration and spoken line (plus the genesis names it
// mentions: the first battle's leader, the arc's antagonist, the heroes, a named place or faction)
// is scored against a fixed vocabulary of cues — Oryx creatures standing in the scene, world props,
// one held-up relic from the items sheet, the land (water, a road, a region kit) and the sky (moon,
// sun, stars, rain, snow, mist, embers). Heading words count double, the spoken line a little less
// than the narration, and the genesis context only enough to tint the land and sky. The best cues
// per slot are placed on the backdrop's grid by a seeded hash of the panel, so a page always paints
// the same way across renders and Back/Next. No embeddings, no network: stems, synonyms, weights.
import { PROPS } from '../../game/tiling/oryx';
import type { BattleBriefView, Folk, Foe, Genesis, Panel, People } from './genesis';

/** Who an actor stands with: the heroes and their folk on the left, foes on the right, beasts on the flanks. */
export type Side = 'party' | 'folk' | 'foe' | 'wild';
export type Water = 'river' | 'pool';
export interface Sky { moon?: 'pale' | 'red'; sun?: boolean; stars?: boolean; rain?: boolean; storm?: boolean; snow?: boolean; mist?: boolean; embers?: boolean }

/** A cue's payload: what it puts on the backdrop. Creature frames are frame A (B is A + 18). */
type Draw =
  | { actor: readonly number[]; side: Side }
  | { prop: readonly number[]; anim?: readonly number[]; n?: number; tower?: boolean }
  | { relic: number }
  | { water?: Water; road?: boolean; kit?: string }
  | { sky: Sky };

/**
 * One vocabulary entry. Words are lowercase tokens; a trailing `*` matches as a stem, a space makes
 * a phrase, a leading `+` marks a group noun (a warband, wolves) that brings several actors.
 */
interface Cue { id: string; words: readonly string[]; draw: Draw }

const STONE = 239;  // the brick dungeon kit's wall base (oryx DUNGEON_ROWS.brick): +4 top end, +5 run, +6 bottom end

/** The vocabulary (~115 cues, ~800 words and phrases). Order breaks ties: earlier entries win. */
const CUES: readonly Cue[] = [
  // ---- foes and monsters
  { id: 'goblins', words: ['goblin*', '+warband', '+warbands', 'hobgoblin*'], draw: { actor: [253, 254, 255], side: 'foe' } },
  { id: 'orcs', words: ['orc', '+orcs', 'orcish', 'uruk*'], draw: { actor: [258, 259], side: 'foe' } },
  { id: 'trolls', words: ['troll*'], draw: { actor: [261], side: 'foe' } },
  { id: 'giants', words: ['giant', '+giants', 'ogre*', 'ettin*', 'cyclops'], draw: { actor: [339], side: 'foe' } },
  { id: 'bandits', words: ['bandit*', 'raider', '+raiders', 'brigand*', 'outlaw*', 'thief', '+thieves', 'cutthroat*', 'highwaym*', 'reaver*', 'marauder*', 'pirate*'], draw: { actor: [37, 110], side: 'foe' } },
  { id: 'gnolls', words: ['gnoll*', 'hyena*'], draw: { actor: [181, 182], side: 'foe' } },
  { id: 'lizardfolk', words: ['lizardfolk', 'lizardman', 'lizardmen', 'lizard-folk', 'saurian*', 'scaled ones'], draw: { actor: [153, 154], side: 'foe' } },
  { id: 'darkelves', words: ['dark elf', 'dark elves', 'drow'], draw: { actor: [115, 114], side: 'foe' } },
  { id: 'cultists', words: ['cultist*', 'clerk', '+clerks', 'tithe-taker*', 'acolyte*', 'zealot*', 'heretic*'], draw: { actor: [38, 74], side: 'foe' } },
  { id: 'wolves', words: ['wolf', '+wolves', 'warg*', 'werewol*', 'hound', '+hounds'], draw: { actor: [229, 231], side: 'wild' } },
  { id: 'bears', words: ['grizzl*', 'cave bear*', 'great bear*', 'she-bear*', 'bearskin*'], draw: { actor: [333], side: 'wild' } },
  { id: 'rats', words: ['rat', '+rats', 'vermin'], draw: { actor: [224, 225], side: 'wild' } },
  { id: 'spiders', words: ['spider*', 'web', 'webs', 'cobweb*'], draw: { actor: [222, 223], side: 'wild' } },
  { id: 'bats', words: ['bat', '+bats'], draw: { actor: [219], side: 'wild' } },
  { id: 'snakes', words: ['snake*', 'serpent*', 'viper*', 'adder*', 'asp'], draw: { actor: [226], side: 'wild' } },
  { id: 'crows', words: ['crow', '+crows', 'raven*', 'rook', '+rooks', 'carrion'], draw: { actor: [234], side: 'wild' } },
  { id: 'birds', words: ['bird', '+birds', 'dove*', 'pigeon*', 'gull*', 'songbird*'], draw: { actor: [232, 233], side: 'wild' } },
  { id: 'scorpions', words: ['scorpion*'], draw: { actor: [336], side: 'wild' } },
  { id: 'worm', words: ['worm', 'worms', 'burrow*', 'burrowing', 'tunnelling', 'digging', 'beast', 'leviathan', 'behemoth'], draw: { actor: [332], side: 'foe' } },
  { id: 'leeches', words: ['leech*', 'bloodsuck*'], draw: { actor: [331], side: 'wild' } },
  { id: 'beetles', words: ['beetle*', 'insect*', 'locust*', 'swarm'], draw: { actor: [227, 228], side: 'wild' } },
  { id: 'slimes', words: ['slime*', 'ooze*', 'jelly'], draw: { actor: [218, 375], side: 'foe' } },
  { id: 'dragon', words: ['dragon*', 'wyrm*', 'drake*', 'wyvern*'], draw: { actor: [325], side: 'foe' } },
  { id: 'demons', words: ['demon*', 'devil*', 'fiend*', 'hellspawn'], draw: { actor: [189, 190], side: 'foe' } },
  { id: 'imps', words: ['imp', 'imps', 'gremlin*'], draw: { actor: [342], side: 'foe' } },
  { id: 'skeletons', words: ['skeleton*', 'bone-men', 'boneless'], draw: { actor: [291, 293], side: 'foe' } },
  { id: 'undead', words: ['undead', 'zombie*', 'corpse*', 'ghoul*', 'walking dead', 'risen'], draw: { actor: [289, 290], side: 'foe' } },
  { id: 'ghosts', words: ['ghost*', 'spirit*', 'phantom*', 'haunt*', 'revenant*', 'drowned', 'apparition*', 'spook*'], draw: { actor: [295], side: 'foe' } },
  { id: 'shadows', words: ['shadow*', 'shade', 'shades', 'darkling*'], draw: { actor: [294], side: 'foe' } },
  { id: 'wraiths', words: ['wraith*', 'specter*', 'spectre*', 'banshee*'], draw: { actor: [371, 372], side: 'foe' } },
  { id: 'mummy', words: ['mumm*', 'embalm*'], draw: { actor: [296], side: 'foe' } },
  { id: 'vampire', words: ['vampir*', 'nosferatu'], draw: { actor: [301], side: 'foe' } },
  { id: 'necromancer', words: ['necromanc*', 'lich', 'liches', 'deathless'], draw: { actor: [298], side: 'foe' } },
  { id: 'witch', words: ['witch*', 'hag', 'hags', 'crone*'], draw: { actor: [304], side: 'foe' } },
  { id: 'sorcerer', words: ['sorcer*', 'warlock*', 'wizard*', 'mage', 'mages', 'magus', 'conjur*', 'enchant*'], draw: { actor: [299], side: 'foe' } },
  { id: 'deathknight', words: ['death knight*', 'black knight*', 'dread knight*', 'fallen knight*'], draw: { actor: [265], side: 'foe' } },
  { id: 'golem', words: ['golem*', 'construct*', 'statue walks', 'stone man'], draw: { actor: [191], side: 'foe' } },
  { id: 'elemental', words: ['elemental*'], draw: { actor: [268, 269], side: 'foe' } },
  { id: 'treant', words: ['treant*', 'ent', 'ents', 'walking tree*'], draw: { actor: [197], side: 'wild' } },
  { id: 'wisps', words: ['wisp*', 'will-o*', 'corpse-light*', 'marsh-light*', 'lights in the'], draw: { actor: [361, 362], side: 'wild' } },
  { id: 'lizardfolk', words: ['lizard*', 'saurian*', 'scaled ones'], draw: { actor: [153, 154], side: 'foe' } },
  { id: 'gnolls', words: ['gnoll*', 'hyena*'], draw: { actor: [181, 182], side: 'foe' } },
  { id: 'minotaur', words: ['minotaur*', 'bull-headed'], draw: { actor: [185], side: 'foe' } },
  { id: 'yeti', words: ['yeti*', 'snow-beast*', 'sasquatch'], draw: { actor: [329], side: 'foe' } },
  { id: 'djinn', words: ['djinn*', 'genie*', 'efreet*', 'ifrit*'], draw: { actor: [196], side: 'foe' } },
  { id: 'fae', words: ['fairy', '+fairies', 'fae', 'fey', 'pixie*', 'sprite', 'sprites'], draw: { actor: [341], side: 'wild' } },
  { id: 'eye', words: ['eye in the', 'watcher*', 'all-seeing', 'beholder*'], draw: { actor: [369], side: 'foe' } },
  { id: 'mimic', words: ['mimic*'], draw: { actor: [198], side: 'foe' } },
  // docs/109: the new peoples' own words
  { id: 'gorgon', words: ['gorgon*', 'medusa*', 'petrif*', 'stone gaze', 'snake-haired'], draw: { actor: [157], side: 'foe' } },
  { id: 'frostcourt', words: ['frost witch*', 'snow queen*', 'ice queen*', 'winter court', 'rime*', 'hoarfrost'], draw: { actor: [305, 366], side: 'foe' } },
  { id: 'duergar', words: ['duergar', 'grey dwar*', 'gray dwar*', 'deep dwar*'], draw: { actor: [112, 111], side: 'foe' } },
  { id: 'menatarms', words: ['men-at-arms', 'man-at-arms', 'tyrant*', 'mercenar*', 'sellsword*', 'pikem*', 'crossbowm*', 'legion*'], draw: { actor: [85, 87], side: 'foe' } },
  { id: 'cyclopes', words: ['cyclopes', 'one-eyed giant*', 'hill giant*'], draw: { actor: [263, 264], side: 'foe' } },
  { id: 'drakes', words: ['wyrmling*', 'dragonet*', 'brood of'], draw: { actor: [326, 328], side: 'foe' } },
  { id: 'scarabs', words: ['scarab*', 'pharaoh*', 'tomb-king*', 'sarcophag*'], draw: { actor: [297, 227], side: 'foe' } },
  { id: 'artificer', words: ['artificer*', 'tinker*', 'clockwork', 'automat*'], draw: { actor: [162, 192], side: 'foe' } },
  { id: 'mandrake', words: ['mandrake*', 'coven*'], draw: { actor: [364, 306], side: 'foe' } },
  // ---- people
  { id: 'king', words: ['king', 'kings', 'lord', 'lords', 'duke', 'earl', 'baron', 'monarch', 'regent', 'steward', 'emperor', 'warlord'], draw: { actor: [45], side: 'folk' } },
  { id: 'queen', words: ['queen*', 'lady', 'duchess', 'baroness', 'empress', 'countess'], draw: { actor: [46], side: 'folk' } },
  { id: 'prince', words: ['prince', 'princes'], draw: { actor: [47], side: 'folk' } },
  { id: 'princess', words: ['princess*', 'heir', 'heiress'], draw: { actor: [48], side: 'folk' } },
  { id: 'knights', words: ['knight', '+knights', 'rider', '+riders', 'paladin*', 'champion*', 'horseman', '+horsemen', 'cavalier*'], draw: { actor: [51, 54], side: 'folk' } },
  { id: 'guards', words: ['guard', '+guards', 'the watch', 'watchman', '+watchmen', 'warden', '+wardens', 'soldier', '+soldiers', 'sentry', 'sentries', 'garrison', 'militia', 'reeve', 'sheriff', 'constable*', 'captain'], draw: { actor: [49, 50, 52], side: 'folk' } },
  { id: 'merchants', words: ['merchant*', 'trader*', 'peddler*', 'pedlar*', 'shopkeep*', 'baker*', 'innkeep*', 'tavern-keep*', 'miller*', 'fortune-hunter*', 'smith', 'blacksmith*'], draw: { actor: [41, 77], side: 'folk' } },
  { id: 'clergy', words: ['priest*', 'abbess*', 'abbot*', 'abbey', 'monk', 'monks', 'nun', 'nuns', 'bishop*', 'chaplain*', 'friar*', 'cleric*', 'pilgrim*'], draw: { actor: [44, 5], side: 'folk' } },
  { id: 'seer', words: ['seer*', 'prophet*', 'oracle*', 'astrologer*', 'star-keeper*', 'sage', 'scholar*', 'mapmaker*', 'cartographer*', 'alchemist*', 'hermit*', 'mystic*'], draw: { actor: [80, 79], side: 'folk' } },
  // docs/104: the bard's and the druid's kind in the story's words (the commissioned bard is 404, the shaman 6)
  { id: 'minstrels', words: ['bard', '+bards', 'minstrel*', 'troubadour*', 'lute-player*', 'piper', '+pipers', 'singer*', 'balladeer*'], draw: { actor: [404], side: 'folk' } },
  { id: 'druids', words: ['druid*', 'shaman*', 'wise woman', 'wise man', 'green man'], draw: { actor: [6], side: 'folk' } },
  { id: 'strangers', words: ['stranger*', 'hooded', 'cloaked', 'cloak', 'grey cloak', 'masked', 'assassin*', 'spy', 'spies'], draw: { actor: [38, 109], side: 'folk' } },
  { id: 'villagers', words: ['villager*', '+townsfolk', '+folk', '+people', '+children', 'child', '+families', 'family', 'farmer*', 'crowd', 'passer-by', 'peasant*', 'elder', '+elders', 'mother', 'father', 'daughter', 'son', 'widow*', 'neighbour*', 'neighbor*', 'refugee*'], draw: { actor: [39, 40, 75, 76], side: 'folk' } },
  { id: 'dwarves', words: ['dwarf', '+dwarves', 'dwarven'], draw: { actor: [111, 112], side: 'folk' } },
  { id: 'elves', words: ['elf', '+elves', 'elven', 'elfin'], draw: { actor: [145, 147], side: 'folk' } },
  // ---- props
  { id: 'graves', words: ['grave', 'graves', 'graveyard*', 'tomb', 'tombs', 'tombstone*', 'barrow*', 'buried', 'burial*', 'bury', 'coffin*', 'crypt*', 'churchyard*', 'cemeter*', 'headstone*', 'mausole*'], draw: { prop: [PROPS.tombstone, PROPS.tombstoneBroken, 265], n: 3 } },
  { id: 'bones', words: ['bone', 'bones', 'skull*', 'carcass*', 'remains', 'ossuar*', 'skeletal'], draw: { prop: [PROPS.bones[0], PROPS.skullPile, PROPS.bonePiles[0], PROPS.bones[2]], n: 3 } },
  { id: 'tower', words: ['tower*', 'keep', 'castle*', 'fortress*', 'citadel*', 'spire*', 'rampart*', 'battlement*', 'stronghold*', 'fort', 'bastion*', 'turret*', 'watchtower*', 'lighthouse*'], draw: { prop: [STONE + 4, STONE + 5, STONE + 6], tower: true } },
  { id: 'ruins', words: ['ruin*', 'rubble', 'crumbl*', 'collapsed', 'toppled', 'broken walls', 'shattered', 'razed', 'sacked'], draw: { prop: [PROPS.rubble, PROPS.paleRubble, PROPS.statues.lying, PROPS.urns.greyBroken], n: 3 } },
  { id: 'wagons', words: ['wagon*', 'cart', 'carts', 'caravan*', 'cargo', 'supplies', 'goods', 'convoy*', 'baggage', 'fire-wagons', 'tomb-goods'], draw: { prop: [PROPS.barrel[0], PROPS.chest.closed, PROPS.barrel[1], PROPS.bucket[0]], n: 4 } },
  { id: 'treasure', words: ['treasure*', 'gold', 'golden', 'hoard*', 'coin', 'coins', 'riches', 'loot*', 'plunder*', 'fortune', 'tithe', 'tithes', 'ransom'], draw: { prop: [PROPS.goldPile, PROPS.chest.gold], n: 2 } },
  { id: 'strongbox', words: ['strongbox*', 'chest', 'chests', 'box', 'boxes', 'coffer*', 'casket*', 'reliquar*', 'vault*'], draw: { prop: [PROPS.chest.closed, 325], n: 1 } },
  { id: 'fire', words: ['fire', 'fires', 'flame*', 'burn*', 'blaze*', 'ablaze', 'bonfire*', 'pyre*', 'inferno', 'aflame', 'smoke', 'smoking', 'torched', 'kindl*', 'campfire*'], draw: { prop: [PROPS.fire[0]], anim: PROPS.fire, n: 3 } },
  { id: 'light', words: ['torch', 'torches', 'lamp', 'lamps', 'lantern*', 'candle*', 'hearth*', 'coal', 'coals', 'brazier*', 'beacon*', 'iron jars'], draw: { prop: [PROPS.brazier[0]], anim: PROPS.brazier, n: 2 } },
  { id: 'well', words: ['the well', 'wells', 'village well', 'wellspring*', 'cistern*'], draw: { prop: [PROPS.well], n: 1 } },
  { id: 'fountain', words: ['fountain*', 'basin', 'basins'], draw: { prop: [PROPS.pedestals.fountain], n: 1 } },
  { id: 'statues', words: ['statue*', 'idol*', 'effig*', 'monument*', 'colossus', 'carving*', 'likeness'], draw: { prop: [PROPS.statues.knight, PROPS.statues.hooded, PROPS.statues.archer], n: 2 } },
  { id: 'shrine', words: ['shrine*', 'altar*', 'chapel*', 'temple*', 'church*', 'sanctum*', 'sanctuar*', 'holy', 'relic', 'relics', 'bell', 'bells', 'bell-ropes', 'abbey'], draw: { prop: [PROPS.pedestals.altar, PROPS.statues.hooded, PROPS.pedestals.bowl], n: 2 } },
  { id: 'throne', words: ['throne*', 'court', 'courts', 'great hall', 'crowned', 'reign*', 'rules', 'ruled', 'usurp*', 'succession'], draw: { prop: [PROPS.furniture.throne, PROPS.bannerRed[0]], n: 2 } },
  { id: 'banners', words: ['banner*', 'flag', 'flags', 'standard', 'standards', 'colours', 'colors', 'heraldry', 'pennant*', 'crest*', 'sigil*'], draw: { prop: [PROPS.bannerRed[0], PROPS.bannerBlue[0]], n: 2 } },
  { id: 'market', words: ['market*', 'square', 'stall', 'stalls', 'inn', 'inns', 'tavern*', 'alehouse*', 'cellar*', 'storehouse*', 'granary', 'larder', 'loaves', 'bread', 'grain', 'harvest*'], draw: { prop: [PROPS.barrel[0], PROPS.bucket[1], PROPS.barrel[1]], n: 3 } },
  { id: 'urns', words: ['urn', 'urns', 'jar', 'jars', 'pot', 'pots', 'amphora*', 'vessel*'], draw: { prop: [PROPS.urns.grey[0], PROPS.urns.red[0], PROPS.urns.blue[0]], n: 3 } },
  { id: 'cauldron', words: ['cauldron*', 'brew*', 'potion*', 'poison*', 'elixir*', 'plague', 'sickness', 'fever'], draw: { prop: [PROPS.cauldron[0]], anim: PROPS.cauldron, n: 1 } },
  { id: 'pit', words: ['pit', 'pits', 'hole', 'holes', 'chasm*', 'crack', 'cracks', 'cracked', 'split', 'splitting', 'rift', 'sinkhole*', 'crater*', 'bulge', 'fissure*', 'trapdoor*', 'floorboards'], draw: { prop: [580, PROPS.rubble], n: 2 } },
  { id: 'stones', words: ['standing stone*', 'stone ring', 'stone circle', 'barrow-ring', 'menhir*', 'dolmen*', 'cairn*', 'monolith*', 'runestone*'], draw: { prop: [PROPS.stoneRing, PROPS.boulder, PROPS.rocksWhite[1]], n: 3 } },
  { id: 'rocks', words: ['rock', 'rocks', 'boulder*', 'crag*', 'hills', 'hill', 'moor', 'moors', 'cliff*', 'quarry', 'mountain*', 'peak*', 'scree'], draw: { prop: [PROPS.boulder, PROPS.rocks, PROPS.rocksWhite[0]], n: 2 } },
  { id: 'flowers', words: ['flower*', 'garden*', 'meadow*', 'blossom*', 'bloom*', 'roses', 'orchard*', 'field', 'fields'], draw: { prop: [PROPS.flowers[0], PROPS.flowers[1], PROPS.flowers[2]], n: 3 } },
  { id: 'deadwood', words: ['dead tree*', 'blight*', 'wither*', 'rot', 'rotting', 'barren', 'gallows', 'gibbet*', 'hanged', 'scorched', 'ashen'], draw: { prop: [PROPS.trees.dead, PROPS.bushSmall[2]], n: 2 } },
  { id: 'felled', words: ['felled', 'stump*', 'logging', 'woodcutter*', 'axe-men', 'tree across', 'barricade*', 'fence*', 'palisade*', 'stockade*'], draw: { prop: [PROPS.trees.stump, 986, 981], n: 3 } },
  { id: 'crystals', words: ['crystal*', 'shard', 'shards', 'geode*'], draw: { prop: [PROPS.crystals.orange, PROPS.crystals.grey[0]], n: 2 } },
  { id: 'portal', words: ['portal*', 'vortex*', 'gateway', 'doorway*', 'hole in the sky', 'tear in the', 'otherworld*'], draw: { prop: [PROPS.doors.portal], n: 1 } },
  { id: 'blood', words: ['blood*', 'bloody', 'slaughter*', 'massacre*', 'butcher*'], draw: { prop: [PROPS.blood[0], PROPS.bloodPool[0]], n: 2 } },
  { id: 'cacti', words: ['cact*', 'desert scrub', 'thornbush*'], draw: { prop: [PROPS.cacti[0], PROPS.cacti[1]], n: 2 } },
  { id: 'library', words: ['ledger*', 'book', 'books', 'library', 'librar*', 'archive*', 'deed', 'deeds', 'charts', 'records'], draw: { prop: [PROPS.furniture.desk[1], PROPS.furniture.bookshelf], n: 2 } },
  // ---- the relic held up in the middle of the scene (items sheet)
  { id: 'crown', words: ['crown', 'crowns', 'crown-mark', 'diadem*', 'circlet*', 'coronet*', 'tiara*'], draw: { relic: 149 } },
  { id: 'sword', words: ['sword', 'swords', 'blade of', 'longsword*', 'greatsword*', 'sabre*'], draw: { relic: 200 } },
  { id: 'key', words: ['key', 'keys', 'keystone'], draw: { relic: 138 } },
  { id: 'tome', words: ['ledger*', 'tome*', 'book', 'grimoire*', 'spellbook*', 'black book', 'chronicle*'], draw: { relic: 67 } },
  { id: 'scroll', words: ['scroll*', 'map', 'maps', 'chart', 'star-charts', 'letter', 'letters', 'decree*', 'deed', 'deeds', 'charter*', 'writ', 'tablet', 'prophecy'], draw: { relic: 136 } },
  { id: 'lantern', words: ['lantern*', 'the last flame', 'one coal', 'ember'], draw: { relic: 148 } },
  { id: 'ring', words: ['ring', 'rings', 'signet*', 'seal', 'seals'], draw: { relic: 76 } },
  { id: 'amulet', words: ['amulet*', 'necklace*', 'pendant*', 'talisman*', 'locket*', 'charm', 'jewel*', 'gem', 'gems'], draw: { relic: 23 } },
  { id: 'fallenstar', words: ['fallen star', 'star fell', 'falling star', 'shooting star', 'starstone*', 'one star', 'the star', 'meteor*'], draw: { relic: 100 } },
  { id: 'orb', words: ['orb', 'orbs', 'sphere*', 'scrying', 'crystal ball'], draw: { relic: 89 } },
  { id: 'heart', words: ['heart', 'hearts', 'heartbeat*', 'heartstone*'], draw: { relic: 85 } },
  { id: 'skull', words: ['skull', 'death mask', 'death-mask'], draw: { relic: 133 } },
  { id: 'purse', words: ['purse*', 'coin-purse', 'bounty', 'reward', 'wages'], draw: { relic: 72 } },
  { id: 'potion', words: ['potion*', 'elixir*', 'vial*', 'phial*', 'antidote*', 'cure'], draw: { relic: 1 } },
  // ---- the land
  { id: 'river', words: ['river*', 'stream*', 'brook*', 'creek*', 'ford', 'fords', 'rapids', 'riverbank*', 'upstream', 'downstream', 'running backwards'], draw: { water: 'river' } },
  { id: 'pool', words: ['mere', 'meres', 'lake*', 'pond*', 'pool', 'pools', 'flood*', 'weir*', 'dam', 'dams', 'dammed', 'sluice*', 'water', 'waters', 'drown*', 'loch', 'tarn', 'reservoir', 'millpond', 'boat', 'boats', 'fish'], draw: { water: 'pool' } },
  { id: 'fen', words: ['fen', 'fens', 'bog*', 'swamp*', 'marsh*', 'mire*', 'quagmire', 'reeds', 'reed-beds', 'wetland*', 'bayou', 'slough'], draw: { water: 'pool', kit: 'mirewood' } },
  { id: 'road', words: ['road', 'roads', 'roadside', 'lane', 'lanes', 'path', 'paths', 'highway*', 'track', 'tracks', 'trail', 'causeway', 'crossroad*', 'king\'s way'], draw: { road: true } },
  { id: 'snowland', words: ['snow*', 'ice', 'icy', 'frozen', 'frost*', 'winter', 'wintry', 'midwinter', 'glacier*', 'tundra', 'blizzard*', 'sleet', 'hoarfrost'], draw: { kit: 'winterwood' } },
  { id: 'desert', words: ['desert*', 'sand', 'sands', 'dune*', 'waste', 'wastes', 'oasis', 'salt flat*'], draw: { kit: 'dunes' } },
  { id: 'pines', words: ['pine*', 'fir', 'firs', 'spruce', 'evergreen*', 'taiga'], draw: { kit: 'pinewood' } },
  { id: 'jungle', words: ['jungle*', 'rainforest*', 'vine', 'vines', 'canopy'], draw: { kit: 'jungle' } },
  { id: 'underdark', words: ['underdark', 'fungus', 'fungi', 'glowcap*', 'abyss*', 'stalactite*', 'stalagmite*', 'deep dark'], draw: { kit: 'underdark' } },
  { id: 'ashlands', words: ['ashlands', 'volcano*', 'volcanic', 'lava', 'magma', 'obsidian', 'caldera*', 'basalt', 'fire mountain*', 'fire-mountain*'], draw: { kit: 'ashlands' } },
  // ---- the sky
  { id: 'redmoon', words: ['red moon', 'blood moon', 'moon rose red', 'crimson moon', 'moon red'], draw: { sky: { moon: 'red' } } },
  { id: 'moon', words: ['moon', 'moons', 'moonlight', 'moonlit', 'midnight', 'night', 'nights', 'tonight'], draw: { sky: { moon: 'pale' } } },
  { id: 'sun', words: ['sun', 'sunrise', 'dawn', 'daybreak', 'first light', 'morning', 'sunup', 'noon', 'midday', 'breakfast'], draw: { sky: { sun: true } } },
  { id: 'stars', words: ['star', 'stars', 'starlight', 'starry', 'constellation*', 'heavens', 'night sky', 'comet*'], draw: { sky: { stars: true } } },
  { id: 'storm', words: ['storm*', 'thunder*', 'lightning', 'tempest*', 'gale*', 'squall*'], draw: { sky: { storm: true, rain: true } } },
  { id: 'rain', words: ['rain*', 'downpour', 'drizzle', 'wet', 'soaked', 'deluge'], draw: { sky: { rain: true } } },
  { id: 'snowfall', words: ['snow', 'snowing', 'snowfall', 'snowflake*', 'blizzard*', 'flurr*', 'sleet'], draw: { sky: { snow: true } } },
  { id: 'mist', words: ['fog*', 'mist*', 'haze', 'hazy', 'murk*', 'vapour*', 'vapor*', 'steam*'], draw: { sky: { mist: true } } },
  { id: 'embers', words: ['ember', 'embers', 'ash', 'ashes', 'cinder*', 'sparks', 'smoke', 'smoulder*', 'smolder*', 'burning'], draw: { sky: { embers: true } } },
];

/** How much a match in each field counts (docs/21 §7: the heading names the page). */
const WEIGHT = { heading: 2, text: 1, line: 0.8, context: 0.35 } as const;
/** The score a cue needs to put something on the stage; the land and sky take a lighter touch. */
const NEEDS = { actor: 1, prop: 1, relic: 1, land: 0.5, sky: 0.8 } as const;
const MAX = { actorCues: 3, propCues: 4, folk: 3, foe: 4, wild: 3, party: 4 } as const;

interface Pattern { words: string[]; stem: boolean; group: boolean }
const compile = (w: string): Pattern => {
  const group = w.startsWith('+');
  const body = group ? w.slice(1) : w;
  const stem = body.endsWith('*');
  return { words: (stem ? body.slice(0, -1) : body).split(' '), stem, group };
};
const COMPILED = CUES.map((c) => ({ cue: c, patterns: c.words.map(compile) }));

/** Lowercase word tokens; hyphenated words count whole and by parts ("crown-mark" → crown-mark, crown, mark). */
export function tokens(text: string): string[] {
  return tokenise(text).toks;
}
/** The tokens and which of them are parts of a compound (a part counts for less: "barrow-ring" is no ring). */
function tokenise(text: string): { toks: string[]; part: boolean[] } {
  const toks: string[] = [], part: boolean[] = [];
  for (const raw of text.toLowerCase().replace(/[\u2018\u2019]/g, "'").split(/[^a-z0-9'_-]+/)) {
    const w = raw.replace(/^['-]+|['-]+$/g, '').replace(/'s$/, '');
    if (!w) continue;
    toks.push(w); part.push(false);
    if (w.includes('-')) for (const p of w.split('-').filter(Boolean)) { toks.push(p); part.push(true); }
  }
  return { toks, part };
}

/** Plural or group: "goblins", "wolves", "the warband" bring a band rather than one. */
const pluralOf = (tok: string, word: string) => tok !== word && /(s|men|ren|ves)$/.test(tok) && !word.endsWith('s');

/** Words a simile hangs on: "glowing like a dragon's teeth" puts no dragon on stage. */
const likeness = (t: string, next: string | undefined) => (t === 'like' && ['a', 'an', 'the', 'some'].includes(next ?? '')) || (t === 'as' && ['a', 'an', 'if', 'though'].includes(next ?? ''));
const SIMILE_SPAN = 4, SIMILE_WEIGHT = 0.3, PART_WEIGHT = 0.6;

interface Hit { score: number; many: boolean }
function scan(text: string, weight: number, hits: Map<string, Hit>) {
  const { toks, part } = tokenise(text);
  const damp: number[] = part.map((p) => (p ? PART_WEIGHT : 1));
  toks.forEach((t, i) => { if (likeness(t, toks[i + 1])) for (let k = i + 1; k <= i + SIMILE_SPAN && k < toks.length; k++) damp[k] = Math.min(damp[k]!, SIMILE_WEIGHT); });
  for (const { cue, patterns } of COMPILED) {
    let n = 0, many = false, mult = 0;
    for (const p of patterns) {
      for (let i = 0; i + p.words.length <= toks.length; i++) {
        let ok = true;
        for (let k = 0; k < p.words.length && ok; k++) {
          const t = toks[i + k]!, w = p.words[k]!;
          ok = k === p.words.length - 1 && p.stem ? t.startsWith(w) : t === w;
        }
        if (!ok) continue;
        n++;
        mult = Math.max(mult, damp[i]!);
        if (p.group || pluralOf(toks[i + p.words.length - 1]!, p.words[p.words.length - 1]!)) many = true;
      }
    }
    if (!n) continue;
    // a second mention adds half again; a page that says "fire" five times is still one fire
    const add = weight * mult * (n === 1 ? 1 : 1.5);
    const h = hits.get(cue.id) ?? { score: 0, many: false };
    hits.set(cue.id, { score: h.score + add, many: h.many || many });
  }
}

/** The genesis names a panel may mention, replaced by marker tokens before scanning. */
export interface StoryContext {
  /** `people` is the battle's (docs/170 OV1); a story from before it carries the kit's own. */
  leader?: { name: string; foe: Foe; people?: People; brief?: BattleBriefView };
  villain?: { name: string; title: string };
  /** the realm's and the places' names are proper nouns ("Ravensholt", "the Drowned Fen"): they lend their land, never their creatures */
  realm?: string;
  places?: Array<{ name: string; text: string }>;
  factions?: Array<{ name: string; want: string }>;
  /** the heroes' creature frames (the party stands in when the text names `{heroes}`) */
  party?: readonly number[];
}

export function storyContext(g: Genesis, party: readonly number[] = []): StoryContext {
  return {
    leader: { name: g.battle.leader.name, foe: g.battle.foe, people: g.battle.people, ...(g.battle.brief ? { brief: g.battle.brief } : {}) },
    realm: g.realm.name,
    villain: { name: g.arc.antagonist.name, title: g.arc.antagonist.title },
    places: [g.places.wild, g.places.road, g.places.crossing, g.haven],
    factions: g.factions,
    party,
  };
}

/**
 * How the first battle's people look (docs/170 OV1): the leader's sprite and the band at its back, the
 * frames fk-sim's kin rows give them on the board (`Kin::frame`; the dead follow a necromancer). Goblins
 * keep the docs/22 king or captain; the burrower its intro worm.
 */
export const PEOPLE_LOOK: Record<People, { leader: number; band: readonly number[]; cue?: string; word: string; many: string }> = {
  Goblins: { leader: 256, band: [253, 254, 255], cue: 'goblins', word: 'goblin', many: 'goblins' },
  Orcs: { leader: 259, band: [258, 260], cue: 'orcs', word: 'orc', many: 'orcs' },
  Bandits: { leader: 73, band: [37, 109, 110], cue: 'bandits', word: 'bandit', many: 'bandits' },
  Cultists: { leader: 299, band: [38, 74, 80], cue: 'cultists', word: 'cultist', many: 'cultists' },
  DarkElves: { leader: 118, band: [115, 114, 116], cue: 'darkelves', word: 'dark elf', many: 'dark elves' },
  Dead: { leader: 299, band: [293, 291, 292], cue: 'skeletons', word: 'risen', many: 'dead' },
  Gnolls: { leader: 183, band: [181, 182, 184], cue: 'gnolls', word: 'gnoll', many: 'gnolls' },
  Lizardfolk: { leader: 155, band: [153, 154, 156], cue: 'lizardfolk', word: 'lizardfolk', many: 'lizardfolk' },
  Spider: { leader: 222, band: [], cue: 'spiders', word: 'spider', many: 'spider' },
  Burrower: { leader: 332, band: [], cue: 'worm', word: 'beast', many: 'beast' },
  // docs/109
  Vampires: { leader: 303, band: [75, 301, 302], cue: 'vampire', word: 'vampire', many: 'vampires' },
  Ghosts: { leader: 294, band: [373, 371, 372], cue: 'ghosts', word: 'ghost', many: 'ghosts' },
  Mummies: { leader: 297, band: [296], cue: 'mummy', word: 'mummy', many: 'mummies' },
  Demons: { leader: 188, band: [190, 342, 365], cue: 'demons', word: 'demon', many: 'demons' },
  Golems: { leader: 162, band: [191, 192, 194], cue: 'golem', word: 'golem', many: 'golems' },
  Elementals: { leader: 268, band: [270, 377, 269], cue: 'elemental', word: 'elemental', many: 'elementals' },
  Djinn: { leader: 196, band: [367, 365, 377], cue: 'djinn', word: 'djinn', many: 'djinn' },
  Coven: { leader: 304, band: [364, 306, 305], cue: 'witch', word: 'witch', many: 'witches' },
  Mimics: { leader: 198, band: [198], cue: 'mimic', word: 'mimic', many: 'mimics' },
  Beholder: { leader: 221, band: [370, 369], cue: 'eye', word: 'eye', many: 'eyes' },
  Gorgons: { leader: 157, band: [153, 226, 154], cue: 'gorgon', word: 'gorgon', many: 'gorgons' },
  Minotaurs: { leader: 185, band: [186, 187], cue: 'minotaur', word: 'minotaur', many: 'minotaurs' },
  Cyclopes: { leader: 264, band: [339, 263, 340], cue: 'giants', word: 'giant', many: 'giants' },
  Drakes: { leader: 327, band: [325, 328, 326], cue: 'dragon', word: 'drake', many: 'drakes' },
  Vermin: { leader: 338, band: [227, 336, 228], cue: 'beetles', word: 'bug', many: 'vermin' },
  WinterCourt: { leader: 305, band: [329, 366, 378], cue: 'frostcourt', word: 'frost-thing', many: 'the winter court' },
  Fey: { leader: 152, band: [197, 341, 361], cue: 'fae', word: 'fey', many: 'fey' },
  Duergar: { leader: 112, band: [111, 160, 113], cue: 'duergar', word: 'grey dwarf', many: 'duergar' },
  MenAtArms: { leader: 266, band: [87, 85, 88], cue: 'menatarms', word: 'soldier', many: 'men-at-arms' },
};
/**
 * docs/50: every folk a brief may cast, as fk-sim's `Kin::frame` draws them by role (Screener,
 * Skirmisher, Javelin, Shaman, Beast, leader), with its words.
 */
export const FOLK_LOOK: Record<Folk, { frames: readonly [number, number, number, number, number, number]; word: string; many: string }> = {
  Goblin: { frames: [253, 255, 254, 257, 229, 256], word: 'goblin', many: 'goblins' },
  Orc: { frames: [258, 259, 258, 260, 231, 259], word: 'orc', many: 'orcs' },
  Bandit: { frames: [37, 109, 110, 79, 230, 73], word: 'bandit', many: 'bandits' },
  Cultist: { frames: [38, 74, 74, 80, 224, 299], word: 'cultist', many: 'cultists' },
  DarkElf: { frames: [115, 114, 116, 117, 223, 118], word: 'dark elf', many: 'dark elves' },
  Skeleton: { frames: [293, 291, 292, 298, 195, 265], word: 'skeleton', many: 'skeletons' },
  Zombie: { frames: [289, 290, 289, 296, 224, 296], word: 'shambling corpse', many: 'the shambling dead' },
  Gnoll: { frames: [181, 182, 182, 184, 230, 183], word: 'gnoll', many: 'gnolls' },
  Lizardfolk: { frames: [153, 153, 154, 156, 226, 155], word: 'lizardfolk', many: 'lizardfolk' },
  Wolf: { frames: [229, 230, 229, 231, 231, 231], word: 'wolf', many: 'wolves' },
  Spider: { frames: [223, 222, 222, 223, 223, 222], word: 'spider', many: 'spiders' },
  Troll: { frames: [261, 261, 261, 261, 333, 262], word: 'troll', many: 'trolls' },
  Ooze: { frames: [218, 217, 218, 217, 218, 374], word: 'ooze', many: 'oozes' },
  Beast: { frames: [333, 334, 333, 334, 333, 333], word: 'beast', many: 'beasts' },
  // docs/109
  Vampire: { frames: [75, 301, 302, 302, 219, 303], word: 'vampire', many: 'vampires' },
  Ghost: { frames: [373, 371, 372, 295, 295, 294], word: 'ghost', many: 'ghosts' },
  Mummy: { frames: [296, 296, 296, 297, 227, 297], word: 'mummy', many: 'mummies' },
  Demon: { frames: [190, 342, 365, 189, 342, 188], word: 'demon', many: 'demons' },
  Golem: { frames: [191, 192, 194, 161, 193, 162], word: 'golem', many: 'golems' },
  Elemental: { frames: [268, 270, 377, 269, 368, 268], word: 'elemental', many: 'elementals' },
  Djinn: { frames: [367, 365, 377, 378, 367, 196], word: 'djinn', many: 'djinn' },
  Witch: { frames: [364, 306, 305, 306, 364, 304], word: 'witch', many: 'witches' },
  Mimic: { frames: [198, 198, 198, 198, 198, 198], word: 'mimic', many: 'mimics' },
  Beholder: { frames: [370, 369, 369, 370, 369, 221], word: 'eye', many: 'beholders' },
  Gorgon: { frames: [153, 226, 154, 157, 226, 157], word: 'gorgon', many: 'gorgons' },
  Minotaur: { frames: [186, 187, 187, 187, 186, 185], word: 'minotaur', many: 'minotaurs' },
  Cyclops: { frames: [339, 263, 264, 340, 334, 264], word: 'cyclops', many: 'cyclopes' },
  Drake: { frames: [325, 328, 326, 327, 328, 327], word: 'drake', many: 'drakes' },
  Vermin: { frames: [227, 336, 228, 331, 337, 338], word: 'vermin', many: 'vermin' },
  WinterCourt: { frames: [329, 366, 378, 269, 335, 305], word: 'frost-thing', many: 'the winter court' },
  Fey: { frames: [197, 341, 361, 362, 363, 152], word: 'fey', many: 'fey' },
  Duergar: { frames: [111, 112, 160, 113, 334, 112], word: 'duergar', many: 'duergar' },
  Soldier: { frames: [87, 85, 88, 44, 230, 266], word: 'man-at-arms', many: 'men-at-arms' },
  // docs/117: commissioned arms (game/creatureArt.ts)
  Tentacles: { frames: [913, 914, 915, 913, 914, 915], word: 'tentacle', many: 'tentacles' },
  SandTentacles: { frames: [916, 917, 918, 916, 917, 918], word: 'tentacle', many: 'tentacles' },
};
const ROLE_INDEX: Record<string, number> = { Screener: 0, Skirmisher: 1, Javelin: 2, Shaman: 3, Beast: 4 };
/** The brief's leader as the board draws it (a great beast from below is the giant worm). */
export function briefLeaderFrame(b: BattleBriefView): number {
  const l = b.cast.leader;
  if (l.folk === 'Beast' && l.archetype === 'Lurker') return 332;
  return FOLK_LOOK[l.folk].frames[5];
}
/** The squad at its back, one frame per group as the board draws them. */
export function briefBandFrames(b: BattleBriefView): number[] {
  const out: number[] = [];
  for (const g of b.cast.squad) {
    const f = FOLK_LOOK[g.folk]?.frames[ROLE_INDEX[g.role] ?? 0];
    if (f !== undefined && !out.includes(f)) out.push(f);
  }
  return out.slice(0, 3);
}
/** "Orcs and wolves", "A great spider": who the brief puts on the field, in words. */
export function briefFoeWords(b: BattleBriefView): string {
  const l = b.cast.leader;
  if (!b.cast.squad.length) {
    if (l.folk === 'Beast') return l.archetype === 'Lurker' ? 'A burrowing beast' : 'A great beast';
    if (l.folk === 'Spider' || l.folk === 'Wolf') return `A great ${FOLK_LOOK[l.folk].word}`;
    const w = FOLK_LOOK[l.folk].word;
    return `${/^[aeiou]/.test(w) ? 'An' : 'A'} ${w}`;
  }
  const names: string[] = [];
  for (const g of b.cast.squad) { const n = FOLK_LOOK[g.folk].many; if (!names.includes(n)) names.push(n); }
  const list = names.length > 1 ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}` : names[0]!;
  return list.charAt(0).toUpperCase() + list.slice(1);
}

/** The people a story from before docs/170 fought: the kit's own. */
export const peopleOf = (b: { foe: Foe; people?: People }): People => b.people ?? (b.foe === 'Beast' ? 'Burrower' : 'Goblins');
/** The leader's sprite (docs/22 O3): the people's, a goblin captain for a goblin ambush. */
export function leaderFrame(b: { foe: Foe; people?: People; brief?: BattleBriefView }): number {
  if (b.brief) return briefLeaderFrame(b.brief);
  const p = peopleOf(b);
  return p === 'Goblins' && b.foe === 'Ambush' ? 255 : PEOPLE_LOOK[p].leader;
}
/** The band at the leader's back (none for a beast; a goblin ambush is two kinds). */
export function bandFrames(b: { foe: Foe; people?: People; brief?: BattleBriefView }): readonly number[] {
  if (b.brief) return briefBandFrames(b.brief);
  const p = peopleOf(b);
  return p === 'Goblins' && b.foe === 'Ambush' ? [253, 254] : PEOPLE_LOOK[p].band;
}

export interface CastActor { cue: string; frames: readonly number[]; side: Side; n: number; speaker?: boolean }
export interface CastProp { cue: string; frames: readonly number[]; anim?: readonly number[]; n: number; tower?: boolean }
/** What a panel puts on stage: the best cues per slot, strongest first. Empty when nothing matched. */
export interface Cast {
  cues: string[];
  actors: CastActor[];
  props: CastProp[];
  relic?: { cue: string; item: number };
  water?: Water;
  road?: boolean;
  kit?: string;
  sky: Sky;
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const named = (text: string, name: string | undefined, marker: string) =>
  name && name.trim().length > 2 ? text.replace(new RegExp(`\\b${esc(name.trim())}\\b`, 'gi'), ` ${marker} `) : text;

/** The sprite a named story person wears: the first people/creature cue in their name or title, else a townsperson by hash. */
export function personFrame(words: string, seed = 0): number {
  const hits = new Map<string, Hit>();
  scan(words, 1, hits);
  for (const { cue } of COMPILED) if (hits.has(cue.id) && 'actor' in cue.draw) return cue.draw.actor[0]!;
  const folk = [39, 40, 75, 76, 41, 38];
  return folk[Math.abs(seed) % folk.length]!;
}

/** The cast of one panel (docs/21 §7): the storyteller's words scored against the vocabulary. */
export function castPanel(panel: Pick<Panel, 'heading' | 'text' | 'line'>, ctx: StoryContext = {}): Cast {
  const hits = new Map<string, Hit>();
  const mark = (s: string) => {
    let t = s.replace(/\{heroes\}/gi, ' __heroes__ ');
    t = named(t, ctx.leader?.name, '__leader__');
    t = named(t, ctx.villain?.name, '__villain__');
    for (const p of ctx.places ?? []) t = named(t, p.name, '__place__');
    return named(t, ctx.realm, '__realm__');
  };
  const raw = [panel.heading, panel.text, panel.line?.text ?? ''].join(' ').toLowerCase();
  const fields: Array<[string, number]> = [[mark(panel.heading), WEIGHT.heading], [mark(panel.text), WEIGHT.text]];
  if (panel.line) fields.push([mark(panel.line.text), WEIGHT.line]);
  const all = fields.map(([t]) => t).join(' ').toLowerCase();
  // a named place or faction lends its name and description to the land and the sky
  for (const p of ctx.places ?? []) if (p.name && raw.includes(p.name.toLowerCase())) fields.push([`${p.name} ${p.text}`, WEIGHT.context]);
  if (ctx.realm && raw.includes(ctx.realm.toLowerCase())) fields.push([ctx.realm, WEIGHT.context]);
  for (const f of ctx.factions ?? []) if (f.name && all.includes(f.name.toLowerCase())) fields.push([`${f.name} ${f.want}`, WEIGHT.context]);
  for (const [t, w] of fields) scan(t, w, hits);

  const ranked = COMPILED.map(({ cue }) => ({ cue, hit: hits.get(cue.id) })).filter((r): r is { cue: Cue; hit: Hit } => !!r.hit)
    .sort((a, b) => b.hit.score - a.hit.score);   // stable: ties keep the table's order
  const cast: Cast = { cues: [], actors: [], props: [], sky: {} };
  const has = (re: RegExp) => re.test(all);

  // the genesis people first: the leader and the villain by name, the heroes, the speaker
  const speaker = panel.line?.speaker.trim() ?? '';
  const leaderSpeaks = !!ctx.leader && speaker.toLowerCase() === ctx.leader.name.trim().toLowerCase();
  if (ctx.leader && (has(/__leader__/) || leaderSpeaks)) {
    cast.actors.push({ cue: 'leader', frames: [leaderFrame(ctx.leader)], side: 'foe', n: 1, speaker: leaderSpeaks });
    const band = bandFrames(ctx.leader);
    const many = PEOPLE_LOOK[peopleOf(ctx.leader)].many;
    if (band.length && (has(/\b(warband|raiders|band)\b/) || has(new RegExp(`\\b${esc(many)}\\b`)))) cast.actors.push({ cue: 'band', frames: band, side: 'foe', n: 3 });
    cast.cues.push('leader');
  }
  if (ctx.villain && has(/__villain__/)) {
    cast.actors.push({ cue: 'villain', frames: [personFrame(`${ctx.villain.title} ${ctx.villain.name}`, 4)], side: 'foe', n: 1 });
    cast.cues.push('villain');
  }
  if (has(/__heroes__/) && ctx.party?.length) {
    cast.actors.push({ cue: 'party', frames: ctx.party.slice(0, MAX.party), side: 'party', n: Math.min(ctx.party.length, MAX.party) });
    cast.cues.push('party');
  }
  let voice = -1;
  if (speaker && !leaderSpeaks) {
    let h = 0;
    for (const c of speaker) h = (h * 31 + c.charCodeAt(0)) | 0;
    voice = personFrame(speaker, h);
    cast.actors.push({ cue: 'speaker', frames: [voice], side: 'folk', n: 1, speaker: true });
  }

  let actorCues = 0, propCues = 0;
  const kinds = new Set(cast.actors.map((a) => a.cue));
  for (const { cue, hit } of ranked) {
    const d = cue.draw;
    if ('actor' in d) {
      if (hit.score < NEEDS.actor || actorCues >= MAX.actorCues) continue;
      // the band already stands with its leader
      if (ctx.leader && cue.id === PEOPLE_LOOK[peopleOf(ctx.leader)].cue && (kinds.has('band') || (kinds.has('leader') && ctx.leader.foe === 'Beast'))) continue;
      // the speaker is the one priest the text names, not a second
      if (!hit.many && d.actor.includes(voice)) continue;
      actorCues++;
      cast.actors.push({ cue: cue.id, frames: d.actor, side: d.side, n: hit.many ? Math.min(3, Math.max(2, d.actor.length)) : 1 });
    } else if ('prop' in d) {
      if (hit.score < NEEDS.prop || propCues >= MAX.propCues) continue;
      propCues++;
      cast.props.push({ cue: cue.id, frames: d.prop, anim: d.anim, n: d.tower ? 1 : Math.min(d.n ?? 1, hit.many ? 4 : Math.max(1, Math.ceil((d.n ?? 1) * Math.min(1, hit.score / 2)))), tower: d.tower });
    } else if ('relic' in d) {
      if (hit.score < NEEDS.relic || cast.relic) continue;
      cast.relic = { cue: cue.id, item: d.relic };
    } else if ('sky' in d) {
      if (hit.score < NEEDS.sky) continue;
      const moon = cast.sky.moon;
      Object.assign(cast.sky, d.sky);
      if (moon === 'red') cast.sky.moon = moon;   // the red moon outranks the pale one, mentioned once or often
    } else {
      if (hit.score < NEEDS.land) continue;
      if (d.water && !cast.water) cast.water = d.water;
      if (d.road) cast.road = true;
      if (d.kit && !cast.kit) cast.kit = d.kit;
    }
    cast.cues.push(cue.id);
  }
  return cast;
}

/** True when a panel matched nothing the backdrop would draw (the Scene paints as before). */
export const emptyCast = (c: Cast) => !c.actors.length && !c.props.length && !c.relic && !c.water && !c.road && !c.kit && !Object.keys(c.sky).length;

// ---- placement ----------------------------------------------------------------------------------

/** One thing drawn on the backdrop's grid (x, y in cells; y < 0 stands against the sky). */
export interface Placed { atlas: 'creature' | 'world' | 'item'; frame: number; x: number; y: number; flip?: boolean; anim?: readonly number[]; cue: string }
export interface Blocking { rows: string[]; placed: Placed[]; road: Array<{ x: number; y: number }>; glows: Array<{ x: number; y: number; kind: 'fire' | 'relic' }>; taken: Set<string> }

/**
 * The stage slots, in cells of the 32 × 6 backdrop strip (x 16 is the centre). Rows 1–2 stand clear
 * of the dialogue box; row 3 only at the flanks. The visible window is about x 7–24 at 1280 px.
 */
const SLOTS: Record<'left' | 'right' | 'centre' | 'flank', ReadonlyArray<readonly [number, number]>> = {
  left: [[12, 2], [10, 2], [11, 1], [13, 1], [9, 1], [8, 2], [9, 3], [7, 3]],
  right: [[20, 2], [22, 2], [21, 1], [19, 1], [23, 1], [24, 2], [23, 3], [25, 3]],
  centre: [[16, 2], [15, 1], [17, 1], [14, 2], [18, 2], [16, 1]],
  flank: [[7, 1], [24, 1], [8, 3], [24, 3], [6, 2], [26, 2], [14, 1], [18, 1], [10, 3], [21, 3], [5, 3], [27, 1], [13, 2], [19, 2]],
};

/** mulberry-style hash → [0, 1) */
function rand(seed: number) {
  let t = seed >>> 0;
  return () => {
    t = (t + 0x6d2b79f5) >>> 0;
    let r = Math.imul(t ^ (t >>> 15), t | 1);
    r ^= r + Math.imul(r ^ (r >>> 7), r | 61);
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

/** A stable seed for a panel's text (FNV-1a). */
export function textSeed(...parts: string[]): number {
  let h = 2166136261;
  for (const c of parts.join('\u0001')) { h ^= c.charCodeAt(0); h = Math.imul(h, 16777619); }
  return h >>> 0;
}

/**
 * Lay a cast out on a scene's rows: water and road cut into the ground, actors on their side
 * (heroes and folk left facing right, foes right facing left, beasts on the flanks), the relic on a
 * pedestal in the middle, props round the edges, a tower on the horizon. Deterministic in `seed`.
 */
export function blockScene(cast: Cast, baseRows: readonly string[], seed: number, opts: { hasFills?: boolean } = {}): Blocking {
  const r = rand(seed);
  const rows = baseRows.map((row) => row.split(''));
  const w = rows[0]?.length ?? 32, h = rows.length;
  const set = (x: number, y: number, c: string) => { if (y >= 0 && y < h && x >= 0 && x < w) rows[y]![x] = c; };
  const at = (x: number, y: number) => rows[y]?.[x] ?? 'T';
  const taken = new Set<string>();
  const placed: Placed[] = [];
  const glows: Blocking['glows'] = [];
  const free = (x: number, y: number) => !taken.has(`${x},${y}`) && at(x, y) !== '~';

  // the land: a river winding across the lower strip, or a pool off-centre
  if (cast.water === 'river') {
    const lift = r() < 0.5 ? 0 : 1;
    for (let x = 0; x < w; x++) { const y = 3 + ((x + lift) % 9 < 5 ? 0 : 1); set(x, y, '~'); set(x, y + 1, '~'); }
  } else if (cast.water === 'pool') {
    const cx = r() < 0.5 ? 9 + Math.floor(r() * 3) : 20 + Math.floor(r() * 3);
    for (let y = 2; y < h; y++) for (let x = cx - 4; x <= cx + 4; x++) {
      const dx = (x - cx) / 4.5, dy = (y - 3.4) / 1.6;
      if (dx * dx + dy * dy <= 1) set(x, y, '~');
    }
  }
  const road: Array<{ x: number; y: number }> = [];
  if (cast.road && !opts.hasFills) {
    for (let x = 0; x < w; x++) { const y = x < 9 ? 3 : x < 15 ? 2 : x < 23 ? 3 : 2; if (at(x, y) !== '~') road.push({ x, y }); }
  }

  const take = (slots: ReadonlyArray<readonly [number, number]>, jitter = 2) => {
    // a seeded nudge down the slot list keeps panels from looking stamped out of one mould
    const start = Math.floor(r() * Math.min(jitter, slots.length));
    for (let i = 0; i < slots.length; i++) {
      const [x, y] = slots[(start + i) % slots.length]!;
      if (free(x, y)) { taken.add(`${x},${y}`); if (at(x, y) === 'T' || at(x, y) === ',') set(x, y, '.'); return [x, y] as const; }
    }
    return undefined;
  };

  // the relic, on its pedestal, centre stage
  if (cast.relic) {
    const s = take(SLOTS.centre, 1);
    if (s) { placed.push({ atlas: 'world', frame: PROPS.pedestals.figure, x: s[0], y: s[1], cue: cast.relic.cue }, { atlas: 'item', frame: cast.relic.item, x: s[0], y: s[1] - 0.62, cue: cast.relic.cue }); glows.push({ x: s[0], y: s[1] - 0.5, kind: 'relic' }); }
  }

  // actors: count per side, then fill their slots front to back
  const cap: Record<Side, number> = { party: MAX.party, folk: MAX.folk, foe: MAX.foe, wild: MAX.wild };
  const hasFoe = cast.actors.some((a) => a.side === 'foe');
  for (const a of [...cast.actors].sort((p, q) => Number(!!q.speaker) - Number(!!p.speaker))) {
    const side: 'left' | 'right' | 'flank' = a.side === 'foe' ? 'right' : a.side === 'wild' ? (hasFoe ? 'flank' : 'right') : 'left';
    for (let i = 0; i < a.n && cap[a.side] > 0; i++) {
      const s = take(SLOTS[side], a.speaker || a.cue === 'leader' ? 1 : 2);
      if (!s) break;
      cap[a.side]--;
      placed.push({ atlas: 'creature', frame: a.frames[i % a.frames.length]!, x: s[0], y: s[1], flip: side === 'right' || (side === 'flank' && s[0] > 16), cue: a.cue });
    }
  }

  // props: a tower on the horizon, the rest scattered on the flanks and between the sides
  for (const p of cast.props) {
    if (p.tower) {
      const x = r() < 0.5 ? 8 + Math.floor(r() * 2) : 23 + Math.floor(r() * 2);
      if (taken.has(`${x},0`)) continue;
      taken.add(`${x},0`);
      set(x, 0, '.');
      p.frames.forEach((f, i) => placed.push({ atlas: 'world', frame: f, x, y: i - 2, cue: p.cue }));
      // an open arch at its foot and a banner on its face, so it reads as a keep rather than a pillar
      placed.push({ atlas: 'world', frame: PROPS.doors.archOpen, x, y: 0, cue: p.cue }, { atlas: 'world', frame: PROPS.bannerRed[0], x, y: -1, cue: p.cue });
      continue;
    }
    for (let i = 0; i < p.n; i++) {
      const s = take(i === 0 && !cast.relic ? [...SLOTS.centre, ...SLOTS.flank] : SLOTS.flank, 4);
      if (!s) break;
      placed.push({ atlas: 'world', frame: p.anim ? p.anim[0]! : p.frames[i % p.frames.length]!, anim: p.anim, x: s[0], y: s[1], cue: p.cue });
      if (p.cue === 'fire' || p.cue === 'light') glows.push({ x: s[0], y: s[1], kind: 'fire' });
    }
  }
  for (const c of road) if (!taken.has(`${c.x},${c.y}`) && at(c.x, c.y) === 'T') set(c.x, c.y, '.');
  return { rows: rows.map((row) => row.join('')), placed, road, glows, taken };
}
